import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auditAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Shield, 
  Search,
  Link2,
  CheckCircle,
  AlertTriangle,
  Clock,
  Database,
  Hash
} from 'lucide-react';
import { toast } from 'sonner';

const Audit = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [verifying, setVerifying] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await auditAPI.getLogs();
      setLogs(response.data);
    } catch (error) {
      console.error('Audit fetch error:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyBlock = async (blockId) => {
    try {
      setVerifying(blockId);
      const response = await auditAPI.verifyBlock(blockId);
      if (response.data.verified) {
        toast.success(`Block #${blockId} verified successfully!`);
      } else {
        toast.error(`Block #${blockId} verification failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Verify error:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const getActionColor = (action) => {
    if (action.includes('LOGIN')) return 'bg-blue-100 text-blue-700';
    if (action.includes('CREATE')) return 'bg-emerald-100 text-emerald-700';
    if (action.includes('UPDATE')) return 'bg-amber-100 text-amber-700';
    if (action.includes('DELETE')) return 'bg-red-100 text-red-700';
    if (action.includes('VPN')) return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const filteredLogs = logs.filter(log =>
    log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.user_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.details?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Blockchain Audit Trail
              </h2>
              <p className="text-slate-300">
                Immutable record of all system activities • {logs.length} blocks
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Blocks</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {logs.length}
                </p>
              </div>
              <Database className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Login Events</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {logs.filter(l => l.action?.includes('LOGIN')).length}
                </p>
              </div>
              <Shield className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">VPN Alerts</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {logs.filter(l => l.action?.includes('VPN')).length}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Chain Status</p>
                <p className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verified
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Logs */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Audit Blocks</CardTitle>
              <CardDescription>Cryptographically secured activity records</CardDescription>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
                data-testid="audit-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredLogs.map((log, index) => (
              <div 
                key={log.block_id || index}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                data-testid={`audit-block-${log.block_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Hash className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-slate-900">
                          Block #{log.block_id}
                        </span>
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        User: <span className="font-mono">{log.user_id}</span>
                        {log.details && <span> • {log.details}</span>}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                        {log.previous_hash && (
                          <div className="flex items-center gap-1">
                            <Link2 className="w-3 h-3" />
                            Previous: {log.previous_hash?.slice(0, 12)}...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleVerifyBlock(log.block_id)}
                      disabled={verifying === log.block_id}
                      data-testid={`verify-block-${log.block_id}`}
                    >
                      {verifying === log.block_id ? (
                        <div className="spinner w-3 h-3 mr-2" />
                      ) : (
                        <CheckCircle className="w-3 h-3 mr-2" />
                      )}
                      Verify
                    </Button>
                    <p className="text-xs text-slate-400 font-mono mt-2 max-w-[200px] truncate">
                      {log.hash?.slice(0, 24)}...
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Audit Logs</h3>
              <p className="text-slate-500">Activity logs will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Audit;
